// =============================================================================
// PHOTOS STORE (Zustand)
// =============================================================================

import { create } from 'zustand';
import { api } from '../api/client.js';
import {
  compressImages,
  compressionResultsToFiles,
  getCompressionSummary,
} from '../utils/imageCompression.js';
import { readCaptureDate } from '../utils/readCaptureDate.js';

// =============================================================================
// TYPES
// =============================================================================

export interface Photo {
  id: string;
  storageKey: string;
  thumbnailKey: string | null;
  originalFilename: string | null;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  takenAt: string | null;
  uploadedAt: string;
  uploader: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
  };
  family: { id: string; name: string } | null;
}

export interface Album {
  id: string;
  name: string;
  description: string | null;
  isShared: boolean;
  familyId: string | null;
  photoCount: number;
  coverPhoto: {
    id: string;
    storageKey: string;
    thumbnailKey: string | null;
  } | null;
  owner: {
    id: string;
    displayName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface PhotosState {
  // State
  photos: Photo[];
  albums: Album[];
  selectedAlbum: Album | null;
  selectedPhoto: Photo | null;
  isLoading: boolean;
  isUploading: boolean;
  isCompressing: boolean;
  compressionProgress: { current: number; total: number; filename: string } | null;
  compressionSummary: { original: string; compressed: string; saved: string; ratio: string } | null;
  hasMore: boolean;
  error: string | null;

  // Tracks photos that just finished ML indexing in this session. UI can
  // read this to flip a "searchable" badge without re-fetching the photo
  // list. Populated by the `photo:indexed` socket event.
  recentlyIndexedPhotoIds: Set<string>;

  // Filter state
  currentFamilyId: string | null;
  currentAlbumId: string | null;

  // Actions
  loadPhotos: (options?: { familyId?: string; albumId?: string; reset?: boolean }) => Promise<void>;
  loadMorePhotos: () => Promise<void>;
  loadAlbums: () => Promise<void>;
  uploadPhotos: (files: File[], familyId?: string) => Promise<boolean>;
  deletePhoto: (photoId: string) => Promise<boolean>;
  selectPhoto: (photo: Photo | null) => void;

  // Share from chat
  shareFromChat: (attachmentId: string, familyId: string, albumId?: string) => Promise<boolean>;

  // Album actions
  createAlbum: (name: string, description?: string, familyId?: string) => Promise<string | null>;
  updateAlbum: (albumId: string, updates: { name?: string; description?: string | null; coverPhotoId?: string | null }) => Promise<boolean>;
  deleteAlbum: (albumId: string) => Promise<boolean>;
  selectAlbum: (album: Album | null) => void;
  addToAlbum: (albumId: string, photoIds: string[]) => Promise<boolean>;
  removeFromAlbum: (albumId: string, photoIds: string[]) => Promise<boolean>;

  // Mark a photo as freshly indexed (called from socket listener).
  markPhotoIndexed: (photoId: string) => void;

  clearError: () => void;
  reset: () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const usePhotosStore = create<PhotosState>()((set, get) => ({
  // Initial state
  photos: [],
  albums: [],
  selectedAlbum: null,
  selectedPhoto: null,
  isLoading: false,
  isUploading: false,
  isCompressing: false,
  compressionProgress: null,
  compressionSummary: null,
  hasMore: false,
  error: null,
  recentlyIndexedPhotoIds: new Set<string>(),
  currentFamilyId: null,
  currentAlbumId: null,

  // Load photos
  loadPhotos: async (options) => {
    const { familyId, albumId, reset = true } = options || {};

    set({
      isLoading: true,
      error: null,
      currentFamilyId: familyId || null,
      currentAlbumId: albumId || null,
      ...(reset ? { photos: [] } : {}),
    });

    const result = await api.getPhotos({ limit: 50, familyId, albumId });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({
        isLoading: false,
        photos: result.data.photos,
        hasMore: result.data.hasMore,
      });
    }
  },

  // Load more photos (pagination)
  loadMorePhotos: async () => {
    const { photos, currentFamilyId, currentAlbumId, hasMore, isLoading } = get();
    if (!hasMore || isLoading) return;

    const lastPhoto = photos[photos.length - 1];
    if (!lastPhoto) return;

    set({ isLoading: true });

    const result = await api.getPhotos({
      limit: 50,
      before: lastPhoto.uploadedAt,
      familyId: currentFamilyId || undefined,
      albumId: currentAlbumId || undefined,
    });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({
        isLoading: false,
        photos: [...photos, ...result.data.photos],
        hasMore: result.data.hasMore,
      });
    }
  },

  // Load albums
  loadAlbums: async () => {
    set({ isLoading: true, error: null });

    const result = await api.getAlbums();

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return;
    }

    if (result.data) {
      set({ isLoading: false, albums: result.data.albums });
    }
  },

  // Upload photos (with client-side compression)
  uploadPhotos: async (files, familyId) => {
    set({
      isCompressing: true,
      isUploading: false,
      error: null,
      compressionProgress: { current: 0, total: files.length, filename: '' },
      compressionSummary: null,
    });

    try {
      // Read each file's EXIF capture date from the original now, before
      // compression re-encodes images and strips that metadata. Videos return
      // null here and are filed under their upload date.
      const takenAtByName = new Map<string, string | null>();
      for (const file of files) {
        takenAtByName.set(file.name, await readCaptureDate(file));
      }

      // Videos cannot go through the canvas image compressor; upload them as-is
      // and only compress the images.
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      const videoFiles = files.filter((f) => f.type.startsWith('video/'));

      // Step 1: Compress images client-side
      const compressionResults = await compressImages(
        imageFiles,
        { maxWidth: 1920, maxHeight: 1920, quality: 0.8, outputType: 'image/jpeg' },
        (completed, total, filename) => {
          set({ compressionProgress: { current: completed, total, filename } });
        }
      );

      // Get compression summary for UI
      const summary = getCompressionSummary(compressionResults);

      set({
        isCompressing: false,
        isUploading: true,
        compressionProgress: null,
        compressionSummary: summary.formatted,
      });

      // Step 2: Pair each upload file with the capture date read from its
      // original. compressionResultsToFiles preserves the source name (minus
      // the new extension), so look the date up by the original file name.
      const compressedFiles = compressionResultsToFiles(compressionResults);
      const uploadItems = [
        ...compressedFiles.map((file, i) => ({
          file,
          takenAt: takenAtByName.get(imageFiles[i].name) ?? null,
        })),
        ...videoFiles.map((file) => ({
          file,
          takenAt: takenAtByName.get(file.name) ?? null,
        })),
      ];
      const result = await api.uploadPhotos(uploadItems, familyId);

      if (result.error) {
        set({ isUploading: false, error: result.error.message, compressionSummary: null });
        return false;
      }

      // Refresh photos list
      await get().loadPhotos({ familyId: get().currentFamilyId || undefined });
      set({ isUploading: false, compressionSummary: null });
      return true;
    } catch (error) {
      set({
        isCompressing: false,
        isUploading: false,
        compressionProgress: null,
        compressionSummary: null,
        error: error instanceof Error ? error.message : 'Failed to upload photos',
      });
      return false;
    }
  },

  // Delete photo
  deletePhoto: async (photoId) => {
    const result = await api.deletePhoto(photoId);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Remove from local state
    set((state) => ({
      photos: state.photos.filter((p) => p.id !== photoId),
      selectedPhoto: state.selectedPhoto?.id === photoId ? null : state.selectedPhoto,
    }));

    return true;
  },

  // Select photo (for lightbox)
  selectPhoto: (photo) => { set({ selectedPhoto: photo }); },

  // Share a chat attachment to the family gallery (deduplicated — no file copy)
  shareFromChat: async (attachmentId, familyId, albumId) => {
    const result = await api.shareAttachmentToGallery(attachmentId, familyId, albumId);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Refresh photos if currently viewing this family
    if (get().currentFamilyId === familyId || !get().currentFamilyId) {
      await get().loadPhotos({ familyId: get().currentFamilyId || undefined });
    }

    return true;
  },

  // Create album
  createAlbum: async (name, description, familyId) => {
    set({ isLoading: true, error: null });

    const result = await api.createAlbum({ name, description, familyId });

    if (result.error) {
      set({ isLoading: false, error: result.error.message });
      return null;
    }

    if (result.data) {
      // Refresh albums
      await get().loadAlbums();
      set({ isLoading: false });
      return result.data.album.id;
    }

    set({ isLoading: false });
    return null;
  },

  // Update album
  updateAlbum: async (albumId, updates) => {
    const result = await api.updateAlbum(albumId, updates);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Merge the scalar fields locally. coverPhotoId is not a field on Album
    // (the album carries a denormalised coverPhoto object), so a cover change
    // is reconciled by reloading albums rather than spreading the raw id.
    const { coverPhotoId, ...scalar } = updates;
    set((state) => ({
      albums: state.albums.map((a) =>
        a.id === albumId ? { ...a, ...scalar, updatedAt: new Date().toISOString() } : a
      ),
    }));

    if (coverPhotoId !== undefined) {
      await get().loadAlbums();
    }

    return true;
  },

  // Delete album
  deleteAlbum: async (albumId) => {
    const result = await api.deleteAlbum(albumId);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    set((state) => ({
      albums: state.albums.filter((a) => a.id !== albumId),
      selectedAlbum: state.selectedAlbum?.id === albumId ? null : state.selectedAlbum,
    }));

    return true;
  },

  // Select album
  selectAlbum: (album) => { set({ selectedAlbum: album }); },

  // Add photos to album
  addToAlbum: async (albumId, photoIds) => {
    const result = await api.addPhotosToAlbum(albumId, photoIds);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Update album photo count
    set((state) => ({
      albums: state.albums.map((a) =>
        a.id === albumId ? { ...a, photoCount: a.photoCount + (result.data?.added || 0) } : a
      ),
    }));

    return true;
  },

  // Remove photos from album
  removeFromAlbum: async (albumId, photoIds) => {
    const result = await api.removePhotosFromAlbum(albumId, photoIds);

    if (result.error) {
      set({ error: result.error.message });
      return false;
    }

    // Update album photo count
    set((state) => ({
      albums: state.albums.map((a) =>
        a.id === albumId ? { ...a, photoCount: Math.max(0, a.photoCount - (result.data?.removed || 0)) } : a
      ),
    }));

    return true;
  },

  // Clear error
  markPhotoIndexed: (photoId) => {
    // Use a fresh Set so React re-renders subscribers that read the field.
    const next = new Set(get().recentlyIndexedPhotoIds);
    next.add(photoId);
    set({ recentlyIndexedPhotoIds: next });
  },

  clearError: () => { set({ error: null }); },

  // Reset store on logout (shared-device privacy)
  reset: () =>
    { set({
      photos: [],
      albums: [],
      selectedAlbum: null,
      selectedPhoto: null,
      isLoading: false,
      isUploading: false,
      isCompressing: false,
      compressionProgress: null,
      compressionSummary: null,
      hasMore: false,
      error: null,
      recentlyIndexedPhotoIds: new Set<string>(),
      currentFamilyId: null,
      currentAlbumId: null,
    }); },
}));
